import type { StructuredContent } from '@repo/contracts/multi-use/structured-content/types';
import type { Author } from '@repo/supergraph/base/types';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import type { Person as PersonSchema } from 'schema-dts';
import { NERDWALLET_AUTHOR_ID, ORGANIZATION } from './constants';

export const extractStructuredContent = (
  structuredContent: StructuredContent | StructuredContent[],
): string => {
  if (!structuredContent || isEmpty(structuredContent)) {
    return '';
  }

  if (typeof structuredContent === 'string') {
    return structuredContent;
  }

  if (isArray(structuredContent)) {
    return structuredContent.map(content => extractStructuredContent(content)).join(' ');
  }

  if (typeof structuredContent === 'object') {
    return extractStructuredContent(get(structuredContent, 'props.children', ''));
  }

  return '';
};

export const getAuthorSchema = (person: Author): PersonSchema => {
  const image = last(person.avatarUrls);
  return {
    '@type': 'Person',
    ...(Number(person.id) === NERDWALLET_AUTHOR_ID && { '@id': get(ORGANIZATION, '@id') }),
    ...(image && { image: image.url }),
    ...(person.name && { name: person.name }),
    ...(person.honorificSuffix && { honorificSuffix: person.honorificSuffix }),
    ...(person.jobTitle && { jobTitle: person.jobTitle }),
    ...(person.link && { url: person.link }),
    ...(person.description && { description: person.description }),
    ...(person.award && { award: person.award }),
    ...(person.affiliation && {
      affiliation: {
        '@type': 'Organization',
        name: person.affiliation,
      },
    }),
    ...(person.socialMediaLinks &&
      person.socialMediaLinks.length > 0 && {
        sameAs: person.socialMediaLinks.map(mediaLink => mediaLink.url),
      }),
    ...(person.alumniOf &&
      !Array.isArray(person.alumniOf) && {
        alumniOf: {
          '@type': 'EducationalOrganization',
          name: person.alumniOf,
        },
      }),
  };
};
